(function () {
  class DivvyUpShopPage extends HTMLElement {
    constructor() {
      super();
      this.pageCache = new Map();
    }

    connectedCallback() {
      this.observeStickyHeader();
      this.setupSmoothScroll();
      this.setupAccordion();
      this.setupLoadMore();
    }

    observeStickyHeader() {
      const checkAndUpdate = () => {
        const hasStickyHeader = document.querySelector(
          ".shopify-section-header-sticky:not(.shopify-section-header-hidden)"
        );
        if (hasStickyHeader) {
          this.classList.add("has-sticky-header");
        } else {
          this.classList.remove("has-sticky-header");
        }
      };

      // Initial check
      checkAndUpdate();

      // Use throttled scroll listener instead of mutation observer
      let ticking = false;
      const onScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            checkAndUpdate();
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener("scroll", onScroll, { passive: true });
      this._scrollHandler = onScroll;
    }

    disconnectedCallback() {
      if (this._scrollHandler) {
        window.removeEventListener("scroll", this._scrollHandler);
      }
    }

    setupSmoothScroll() {
      const links = this.querySelectorAll('a[href^="#"]');

      links.forEach((link) => {
        link.addEventListener("click", (e) => {
          e.preventDefault();
          const targetId = link.getAttribute("href");
          const targetElement = document.querySelector(targetId);

          if (targetElement) {
            const targetPosition =
              targetElement.getBoundingClientRect().top + window.scrollY;
            const currentPosition = window.scrollY;
            const isScrollingDown = targetPosition > currentPosition;
            const isMobile = window.innerWidth < 990;

            // Different offsets based on screen size and scroll direction
            let offset;
            if (isMobile) {
              offset = isScrollingDown ? 64 : 120;
            } else {
              offset = isScrollingDown ? 16 : 120;
            }

            const scrollToPosition = targetPosition - offset;

            window.scrollTo({
              top: scrollToPosition,
              behavior: "smooth",
            });
          }
        });
      });
    }

    setupAccordion() {
      const triggers = this.querySelectorAll("[data-accordion-trigger]");

      triggers.forEach((trigger) => {
        trigger.addEventListener("click", () => {
          this.toggleAccordion(trigger);
        });

        trigger.addEventListener("keydown", (e) => {
          this.handleAccordionKeydown(e, trigger);
        });
      });

      this.setupSidebarToggle();
    }

    setupSidebarToggle() {
      const toggleButton = this.querySelector("[data-sidebar-toggle]");
      const closeButton = this.querySelector("[data-sidebar-close]");
      const sidebar = this.querySelector("[data-sidebar]");
      const overlay = this.querySelector("[data-sidebar-overlay]");

      if (!toggleButton || !sidebar || !overlay) return;

      const openSidebar = () => {
        sidebar.classList.add("is-open");
        overlay.hidden = false;
        toggleButton.setAttribute("aria-expanded", "true");
        document.body.style.overflow = "hidden";
      };

      const closeSidebar = () => {
        sidebar.classList.remove("is-open");
        overlay.hidden = true;
        toggleButton.setAttribute("aria-expanded", "false");
        document.body.style.overflow = "";
      };

      toggleButton.addEventListener("click", openSidebar);

      if (closeButton) {
        closeButton.addEventListener("click", closeSidebar);
      }

      overlay.addEventListener("click", closeSidebar);

      // Close on category selection (mobile) - only for actual links, not accordion buttons
      const menuLinks = this.querySelectorAll(
        ".divvyup-shop-page__menu-link:not(.divvyup-shop-page__menu-button), .divvyup-shop-page__submenu-link"
      );
      menuLinks.forEach((link) => {
        link.addEventListener("click", () => {
          if (window.innerWidth < 990) {
            closeSidebar();
          }
        });
      });
    }

    toggleAccordion(trigger) {
      const menuItem = trigger.closest(".divvyup-shop-page__menu-item");
      const content = menuItem.querySelector("[data-accordion-content]");
      const isOpen = trigger.getAttribute("aria-expanded") === "true";

      if (isOpen) {
        trigger.setAttribute("aria-expanded", "false");
        content.hidden = true;
        menuItem.classList.remove("is-open");
        content.style.maxHeight = null;
      } else {
        trigger.setAttribute("aria-expanded", "true");
        content.hidden = false;
        menuItem.classList.add("is-open");
        content.style.maxHeight = content.scrollHeight + "px";
      }
    }

    handleAccordionKeydown(e, trigger) {
      const triggers = Array.from(
        this.querySelectorAll("[data-accordion-trigger]")
      );
      const currentIndex = triggers.indexOf(trigger);

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (currentIndex < triggers.length - 1) {
            triggers[currentIndex + 1].focus();
          }
          break;

        case "ArrowUp":
          e.preventDefault();
          if (currentIndex > 0) {
            triggers[currentIndex - 1].focus();
          }
          break;

        case "Home":
          e.preventDefault();
          triggers[0].focus();
          break;

        case "End":
          e.preventDefault();
          triggers[triggers.length - 1].focus();
          break;
      }
    }

    setupLoadMore() {
      const loadMoreButtons = this.querySelectorAll("[data-load-more]");

      loadMoreButtons.forEach((button) => {
        button.addEventListener("click", (e) => {
          e.preventDefault();
          this.loadMoreProducts(button);
        });
      });
    }

    async loadMoreProducts(button) {
      const section = button.closest(".divvyup-shop-page__collection");
      if (!section) return;

      const collectionUrl = button.dataset.collectionUrl;
      const productsLoaded = parseInt(section.dataset.productsLoaded);
      const totalProducts = parseInt(section.dataset.collectionSize);

      if (productsLoaded >= totalProducts) {
        button.disabled = true;
        return;
      }

      // Set loading state
      const buttonText = button.querySelector("[data-button-text]");
      const loadingText = button.querySelector("[data-loading-text]");
      button.disabled = true;
      buttonText.hidden = true;
      loadingText.hidden = false;

      try {
        // Determine how many products to load: 18 for first click (6->24), 24 for subsequent clicks
        const isFirstLoad = productsLoaded === 6;
        const productsToLoad = isFirstLoad ? 18 : 24;
        const productsPerPage = 16; // Must match with Shopify collection pagination

        // Calculate which pages we need to fetch
        const startIndex = productsLoaded;
        const endIndex = Math.min(startIndex + productsToLoad, totalProducts);

        // Calculate page range needed
        const startPage = Math.floor(startIndex / productsPerPage) + 1;
        const endPage = Math.floor((endIndex - 1) / productsPerPage) + 1;

        const productGrid = section.querySelector(".product-grid");
        if (!productGrid) {
          throw new Error("Product grid not found");
        }

        const allProductsToAdd = [];

        // Fetch all necessary pages in parallel
        const pagePromises = [];
        for (let page = startPage; page <= endPage; page++) {
          const cacheKey = `${collectionUrl}-page-${page}`;

          // Check cache first
          if (this.pageCache.has(cacheKey)) {
            pagePromises.push(Promise.resolve(this.pageCache.get(cacheKey)));
          } else {
            const pagePromise = fetch(`${collectionUrl}?view=ajax&page=${page}`)
              .then((response) => {
                if (!response.ok) {
                  throw new Error(`Failed to load page ${page}`);
                }
                return response.text();
              })
              .then((html) => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, "text/html");
                const pageProducts = Array.from(
                  doc.querySelectorAll(".product-card-wrapper")
                );
                // Cache the page products
                this.pageCache.set(cacheKey, pageProducts);
                return pageProducts;
              });
            pagePromises.push(pagePromise);
          }
        }

        // Wait for all pages to load
        const allPageProducts = await Promise.all(pagePromises);

        // Extract the products we need from each page
        for (let i = 0; i < allPageProducts.length; i++) {
          const pageProducts = allPageProducts[i];
          const page = startPage + i;
          const pageStartIndex = (page - 1) * productsPerPage;

          // Calculate slice indices relative to this page
          const sliceStart = Math.max(0, startIndex - pageStartIndex);
          const sliceEnd = Math.min(productsPerPage, endIndex - pageStartIndex);

          const productsFromThisPage = pageProducts.slice(sliceStart, sliceEnd);
          allProductsToAdd.push(...productsFromThisPage);
        }

        if (allProductsToAdd.length === 0) {
          throw new Error("No more products to load");
        }

        // Find the mobile "Show More" slide to insert before it (if it exists)
        const showMoreSlide = productGrid.querySelector(
          ".divvyup-shop-page__show-more-slide"
        );

        // Add products to the grid
        const collectionId = section.id.replace("collection-", "");
        const sectionId = this.id.replace("Shop-", "");
        const sliderComponent = section.querySelector("slider-component");
        let firstNewProductId = null;

        // Temporarily disable smooth scrolling on mobile
        if (window.innerWidth < 990 && sliderComponent) {
          productGrid.style.scrollBehavior = "auto";
        }

        for (let i = 0; i < allProductsToAdd.length; i++) {
          const product = allProductsToAdd[i];
          const li = document.createElement("li");
          const productIndex = productsLoaded + i + 1;
          li.id = `Slide-${sectionId}-${collectionId}-${productIndex}`;
          li.className = "grid__item slider__slide divvyup-shop-page__product";
          li.innerHTML = product.innerHTML;

          // Store first new product ID
          if (i === 0) {
            firstNewProductId = li.id;
          }

          // Insert before the "Show More" slide on mobile, or append at the end
          if (showMoreSlide) {
            productGrid.insertBefore(li, showMoreSlide);
          } else {
            productGrid.appendChild(li);
          }

          // Trigger animation
          setTimeout(() => {
            li.classList.add("animate--slide-in");
          }, i * 50);
        }

        // Scroll to first new product on mobile, then re-enable smooth scrolling
        if (window.innerWidth < 990 && firstNewProductId && sliderComponent) {
          const firstNewProduct = document.getElementById(firstNewProductId);
          if (firstNewProduct) {
            // Use the slider component's update method to recalculate
            sliderComponent.resetPages();

            // Snap scroll to the first new product
            setTimeout(() => {
              productGrid.scrollTo({
                left: firstNewProduct.offsetLeft - 15,
              });

              // Re-enable smooth scrolling after snap
              setTimeout(() => {
                productGrid.style.scrollBehavior = "";

                // Recalc again so Next button is reset
                sliderComponent.resetPages();
              }, 50);
            }, 100);
          }
        }

        // Update the products loaded count
        const newProductsLoaded = productsLoaded + allProductsToAdd.length;
        section.dataset.productsLoaded = newProductsLoaded.toString();

        // Update or remove the "Show More" button
        if (newProductsLoaded >= totalProducts) {
          // All products loaded - remove all "Show More" buttons
          section.querySelectorAll("[data-load-more]").forEach((btn) => {
            const parentLi = btn.closest(".divvyup-shop-page__show-more-slide");
            if (parentLi) {
              parentLi.remove();
            } else {
              btn.remove();
            }
          });
        } else {
          // More products remain - enable button
          button.disabled = false;
          buttonText.hidden = false;
          loadingText.hidden = true;
        }
      } catch (error) {
        console.error("Error loading more products:", error);

        // Calculate which page the user should be on based on products loaded
        const productsPerPage = 16;
        const targetPage = Math.floor(productsLoaded / productsPerPage) + 1;

        // Redirect to the collection page at the appropriate page
        window.location.href = `${collectionUrl}?page=${targetPage}`;
      }
    }
  }

  customElements.define("divvyup-shop-page", DivvyUpShopPage);
})();
